import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { finalize } from "rxjs/operators";
import { RecetaService, CanalesCompartir } from "app/services/receta.service";
import { PacienteService } from "app/services/paciente.service";
import { CentroMedicoService } from "app/services/centro-medico.service";
import { SpinnerService } from "app/services/spinner.service";
import { MedicamentoSeleccionado } from "app/shared/buscador-medicamentos/buscador-medicamentos.component";
import { CompartirRecetaDialogComponent } from "app/shared/compartir-receta-dialog/compartir-receta-dialog.component";
import { Receta, CrearRecetaRequest } from "app/models/receta.interface";

interface MedicamentoItem extends MedicamentoSeleccionado {
  cantidad: number;
}

interface PacienteOption {
  id: number;
  name: string;
  surname: string;
  numeroDocumento: string;
  obraSocial: string;
  email: string;
  celPais: string;
  celCaract: string;
  celNumero: string;
}

@Component({
  selector: "app-emitir-receta",
  templateUrl: "./emitir-receta.component.html",
  styleUrls: ["./emitir-receta.component.css"],
})
export class EmitirRecetaComponent implements OnInit {
  @Input() pacienteInput: PacienteOption | null = null;
  @Input() centroMedicoIdInput: number | null = null;
  @Input() modoDialog = false;

  @Output() recetaCreada = new EventEmitter<Receta[]>();

  pacientes: PacienteOption[] = [];
  pacientesFiltrados: PacienteOption[] = [];
  pacienteSeleccionado: PacienteOption | null = null;
  filtroPaciente = "";

  medicamentos: MedicamentoItem[] = [];
  mostrarBuscadorMedicamentos = false;

  diagnosisControl = new FormControl("", [
    Validators.required,
    Validators.maxLength(500),
  ]);

  procesando = false;
  recetasGeneradas: Receta[] = [];
  error = "";
  canalesCompartir: CanalesCompartir = {};

  medicoId: number;
  centroMedicoId: number;

  constructor(
    private recetaService: RecetaService,
    private pacienteService: PacienteService,
    private centroMedicoService: CentroMedicoService,
    private spinnerService: SpinnerService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.medicoId = Number(localStorage.getItem("medicoId"));

    if (!this.medicoId || isNaN(this.medicoId)) {
      this.error =
        "No se pudo identificar al médico. Por favor, inicie sesión nuevamente.";
      return;
    }

    if (this.pacienteInput) {
      this.pacienteSeleccionado = this.pacienteInput;
    } else {
      this.cargarPacientes();
    }

    if (this.centroMedicoIdInput) {
      this.centroMedicoId = this.centroMedicoIdInput;
    } else {
      this.cargarCentroMedico();
    }

    this.recetaService.getCanalesCompartir().subscribe({
      next: (canales) => (this.canalesCompartir = canales),
    });
  }

  get puedeCompartir(): boolean {
    return !!this.canalesCompartir.whatsapp || !!this.canalesCompartir.email;
  }

  cargarCentroMedico(): void {
    this.centroMedicoService.mostrarCentroMedico().subscribe({
      next: (data: any) => {
        this.centroMedicoId = data.id;
      },
      error: () => {
        this.centroMedicoId = 0;
      },
    });
  }

  cargarPacientes(): void {
    this.pacienteService.listarPacientesDeMedico(this.medicoId).subscribe({
      next: (data: any) => {
        this.pacientes = (data.pacientes || data || []).map((p: any) => ({
          id: p.id,
          name: p.name || "",
          surname: p.surname || "",
          numeroDocumento: p.numeroDocumento || "",
          obraSocial: p.obraSocial || "particular",
          email: p.email || "",
          celPais: p.celPais || "",
          celCaract: p.celCaract || "",
          celNumero: p.celNumero || "",
        }));
      },
      error: () => {
        this.pacientes = [];
      },
    });
  }

  filtrarPacientes(): void {
    const filtro = this.filtroPaciente.toLowerCase().trim();
    if (!filtro) {
      this.pacientesFiltrados = [];
      return;
    }
    this.pacientesFiltrados = this.pacientes.filter(
      (p) =>
        p.name.toLowerCase().includes(filtro) ||
        p.surname.toLowerCase().includes(filtro) ||
        p.numeroDocumento.includes(filtro),
    );
  }

  seleccionarPaciente(paciente: PacienteOption): void {
    this.pacienteSeleccionado = paciente;
    this.filtroPaciente = "";
    this.pacientesFiltrados = [];
  }

  limpiarPaciente(): void {
    this.pacienteSeleccionado = null;
  }

  toggleBuscadorMedicamentos(): void {
    this.mostrarBuscadorMedicamentos = !this.mostrarBuscadorMedicamentos;
  }

  onMedicamentoSeleccionado(med: MedicamentoSeleccionado): void {
    this.medicamentos.push({
      ...med,
      cantidad: 1,
    });
    this.mostrarBuscadorMedicamentos = false;
  }

  eliminarMedicamento(index: number): void {
    this.medicamentos.splice(index, 1);
  }

  cantidadRecetas(): number {
    return Math.ceil(this.medicamentos.length / 3);
  }

  formularioValido(): boolean {
    return (
      !!this.pacienteSeleccionado &&
      !!this.centroMedicoId &&
      this.medicamentos.length > 0 &&
      this.diagnosisControl.valid &&
      this.medicamentos.every((m) => m.cantidad > 0 && m.cantidad <= 99)
    );
  }

  emitirReceta(): void {
    if (!this.formularioValido()) {
      this.diagnosisControl.markAsTouched();
      const faltan: string[] = [];
      if (!this.pacienteSeleccionado) faltan.push("Paciente");
      if (!this.centroMedicoId) faltan.push("Centro médico");
      if (this.medicamentos.length === 0 || !this.medicamentos.every((m) => m.cantidad > 0 && m.cantidad <= 99)) {
        faltan.push("Medicamentos");
      }
      if (this.diagnosisControl.invalid) faltan.push("Diagnóstico");
      this.error = faltan.length
        ? "Completá los campos obligatorios: " + faltan.join(", ")
        : "Revisá los datos marcados en el formulario";
      return;
    }

    this.procesando = true;
    this.error = "";
    this.recetasGeneradas = [];
    this.spinnerService.mostrarSpinner();

    const request: CrearRecetaRequest = {
      paciente_id: this.pacienteSeleccionado!.id,
      centro_medico_id: this.centroMedicoId,
      diagnosis: this.diagnosisControl.value!,
      medicamentos: this.medicamentos.map((m) => ({
        external_medication_id: m.external_medication_id,
        medication_name: m.medication_name,
        presentation: m.presentation,
        cantidad: m.cantidad,
      })),
    };

    this.recetaService
      .crear(request)
      .pipe(
        finalize(() => {
          this.procesando = false;
          this.spinnerService.ocultarSpinner();
        }),
      )
      .subscribe({
        next: (recetas) => {
          this.recetasGeneradas = recetas;
          this.recetaCreada.emit(recetas);
        },
        error: (err) => {
          this.error = err.error?.error || "Error al emitir la receta";
        },
      });
  }

  nuevaReceta(): void {
    this.pacienteSeleccionado = this.pacienteInput ?? null;
    this.medicamentos = [];
    this.diagnosisControl.reset();
    this.recetasGeneradas = [];
    this.error = "";
  }

  compartirRecetas(): void {
    const p = this.pacienteSeleccionado;
    this.dialog.open(CompartirRecetaDialogComponent, {
      width: "450px",
      data: {
        recetas: this.recetasGeneradas,
        paciente: {
          email: p?.email || "",
          celular:
            (p?.celPais || "") + (p?.celCaract || "") + (p?.celNumero || ""),
        },
        canales: this.canalesCompartir,
      },
    });
  }
}
